/**
 * DropZone: the card a file is dropped on or clicked to choose, the same in
 * every place that takes one (Create, Import, attachments). It says what to
 * drop, what happens next, the rules before anything is dropped (types, count,
 * size), and where the files go; while a drag is over it, it says whether
 * they fit. The logic is `useFileIntake`; this is only the look.
 *
 * On a touch screen there is no drag, so the card is a tap target that says
 * "Choose a file", and an image rule adds "Take a photo" (the camera) beside
 * "Choose from library". No path is drag-only (WCAG 2.5.7).
 */

import {
  Box,
  Button,
  Flex,
  HStack,
  Progress,
  Stack,
  Text,
  VisuallyHidden,
  useMediaQuery,
} from '@chakra-ui/react';
import { useId, useRef, type ChangeEvent, type MouseEvent } from 'react';
import { MAX_UPLOAD_BYTES } from '@bower/shared/schemas/upload.schema';

import { MaterialIcon } from '../../ui/MaterialIcon';
import { TOUCH_PRIMARY_QUERY } from '../../workspace/dnd/useItemDrag';
import type { IntakeDragState } from './types';
import { dragHeadline, type FileIntake } from './useFileIntake';
import { formatMegabytes } from './validateFiles';

interface DropZoneUpload {
  readonly id: string;
  readonly name: string;
  readonly status: 'waiting' | 'uploading' | 'done' | 'skipped' | 'failed';
  /** 0-100 while uploading. */
  readonly progress?: number;
}

interface DropZoneProps {
  readonly intake: FileIntake;
  /** Material symbol for what the file becomes. */
  readonly icon: string;
  /** What to drop, and that clicking works too: "Drop a CSV or XLSX, or click to choose". */
  readonly title: string;
  /** The title on a touch screen, where nothing is dragged. */
  readonly touchTitle?: string;
  /** What happens next, in one line, so nobody drops a file blind. */
  readonly next?: string;
  /** Files this zone is uploading, for the uploading look. */
  readonly uploads?: readonly DropZoneUpload[];
}

type Look = IntakeDragState;

const LOOK: Record<
  Look,
  { border: string; borderStyle: string; bg: string; tileBg: string; ink: string; icon?: string }
> = {
  idle: {
    border: 'border.accent',
    borderStyle: 'dashed',
    bg: 'bg.surface',
    tileBg: 'accent.bg',
    ink: 'text.accent',
  },
  accept: {
    border: 'border.accent',
    borderStyle: 'solid',
    bg: 'accent.bg',
    tileBg: 'brand.primary',
    ink: 'text.onBrand',
    icon: 'south',
  },
  partial: {
    border: 'border.warning',
    borderStyle: 'dashed',
    bg: 'bg.warning',
    tileBg: 'bg.elevated',
    ink: 'text.warning',
    icon: 'rule',
  },
  refuse: {
    border: 'border.default',
    borderStyle: 'dashed',
    bg: 'bg.surface',
    tileBg: 'bg.elevated',
    ink: 'text.secondary',
    icon: 'lock',
  },
};

const UPLOAD_STATUS: Record<
  DropZoneUpload['status'],
  { label: string; color: string; icon: string }
> = {
  waiting: { label: 'Waiting', color: 'text.secondary', icon: 'schedule' },
  uploading: { label: '', color: 'text.secondary', icon: 'upload' },
  done: { label: 'Added', color: 'text.success', icon: 'check_circle' },
  skipped: { label: 'Skipped', color: 'text.warning', icon: 'block' },
  failed: { label: 'Failed', color: 'text.error', icon: 'error' },
};

function ruleChips({ intake }: Pick<DropZoneProps, 'intake'>): string[] {
  const size = formatMegabytes(intake.maxBytes ?? MAX_UPLOAD_BYTES);
  const { sizeLabel } = intake.rule;
  const count =
    intake.maxFiles === 1
      ? ['1 file', sizeLabel ?? `up to ${size}`]
      : [`up to ${intake.maxFiles} files`, sizeLabel ?? `${size} each`];
  return [...intake.rule.typeLabels, ...count];
}

function UploadRow({ upload }: { readonly upload: DropZoneUpload }) {
  const status = UPLOAD_STATUS[upload.status];
  const label =
    upload.status === 'uploading' ? `${Math.round(upload.progress ?? 0)}%` : status.label;
  return (
    <Box
      display="grid"
      gridTemplateColumns="auto minmax(0, 1fr) auto"
      columnGap={2}
      rowGap={1}
      alignItems="center"
      px={2}
      py={1.5}
      borderWidth="1px"
      borderColor={upload.status === 'failed' ? 'border.error' : 'border.default'}
      borderRadius="lg"
      bg="bg.elevated"
      textAlign="left"
      fontSize="sm"
    >
      <MaterialIcon name={status.icon} boxSize={4} color="text.secondary" />
      <Text noOfLines={1} title={upload.name}>
        {upload.name}
      </Text>
      <Text fontSize="xs" color={status.color}>
        {label}
      </Text>
      {upload.status === 'uploading' && (
        <Progress
          gridColumn="2 / 4"
          value={upload.progress ?? 0}
          size="xs"
          borderRadius="full"
          aria-label={upload.name}
          sx={{ '& > div': { bg: 'brand.primary' } }}
        />
      )}
    </Box>
  );
}

function RuleChips({ intake }: Pick<DropZoneProps, 'intake'>) {
  return (
    <HStack spacing={1.5} wrap="wrap" justify="center" mt={1.5}>
      {ruleChips({ intake }).map((chip) => (
        <Text
          key={chip}
          as="span"
          fontFamily="mono"
          fontSize="2xs"
          letterSpacing="0.04em"
          color="text.secondary"
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="full"
          px={2}
        >
          {chip}
        </Text>
      ))}
    </HStack>
  );
}

function Destination({ label }: { readonly label: string }) {
  return (
    <HStack spacing={1} fontSize="xs" color="text.secondary" pt={1}>
      <MaterialIcon name="home" boxSize={3.5} color="text.secondary" />
      <Text as="span">Saving to</Text>
      <Text as="span" fontWeight="semibold" color="text.primary">
        {label}
      </Text>
    </HStack>
  );
}

/** Touch screens, image rules: the camera first, the photo library beside it. Full width under 480px. */
function CameraButtons({
  onTakePhoto,
  onChooseLibrary,
}: {
  readonly onTakePhoto: (event: MouseEvent) => void;
  readonly onChooseLibrary: (event: MouseEvent) => void;
}) {
  return (
    <Stack direction={{ base: 'column', sm: 'row' }} w="full" spacing={2} pt={2}>
      <Button
        flex={1}
        minH="44px"
        leftIcon={<MaterialIcon name="photo_camera" color="inherit" />}
        onClick={onTakePhoto}
      >
        Take a photo
      </Button>
      <Button
        flex={1}
        minH="44px"
        variant="outline"
        leftIcon={<MaterialIcon name="photo_library" color="inherit" />}
        onClick={onChooseLibrary}
      >
        Choose from library
      </Button>
    </Stack>
  );
}

/** The headline and the line under it, for the look the zone is in. */
function zoneCopy(intake: FileIntake, look: Look, instruction: string, next?: string) {
  if (look === 'idle') return { headline: instruction, detail: next };
  // A read-only zone at rest wears the refusal look without a drag.
  const drag = intake.drag.state === 'idle' ? { ...intake.drag, state: look } : intake.drag;
  return {
    headline: dragHeadline(drag, intake.destinationLabel, Boolean(intake.readOnlyReason)),
    detail: intake.drag.reason ?? intake.readOnlyReason,
  };
}

export function DropZone({
  intake,
  icon,
  title,
  touchTitle = 'Choose a file',
  next,
  uploads,
}: DropZoneProps) {
  const [isTouch] = useMediaQuery(TOUCH_PRIMARY_QUERY);
  const cameraRef = useRef<HTMLInputElement>(null);
  const describedBy = useId();
  const readOnly = Boolean(intake.readOnlyReason);
  const look: Look = intake.drag.state === 'idle' && readOnly ? 'refuse' : intake.drag.state;
  const style = LOOK[look];
  const offersCamera = isTouch && Boolean(intake.rule.camera) && !readOnly;

  // The button's name stays the instruction; what a drag does goes to the live region.
  const instruction = isTouch ? touchTitle : title;
  const { headline, detail } = zoneCopy(intake, look, instruction, next);

  const takePhoto = (event: MouseEvent) => {
    event.stopPropagation();
    cameraRef.current?.click();
  };
  const chooseFromLibrary = (event: MouseEvent) => {
    event.stopPropagation();
    intake.open();
  };
  const onCamera = (event: ChangeEvent<HTMLInputElement>) => {
    intake.addFiles(Array.from(event.target.files ?? []), 'camera');
    event.target.value = '';
  };

  return (
    <Box>
      <Stack
        {...intake.getRootProps({
          // With its own buttons inside (the camera pair), the card is a
          // labelled group, not a button: a button may not contain buttons.
          ...(offersCamera ? { role: 'group', tabIndex: -1 } : { role: 'button' }),
          'aria-label': instruction,
          'aria-describedby': describedBy,
          'aria-disabled': readOnly || undefined,
        })}
        data-intake-state={look}
        align="center"
        textAlign="center"
        spacing={1.5}
        px={4}
        py={5}
        borderWidth="2px"
        borderStyle={style.borderStyle}
        borderColor={style.border}
        borderRadius="xl"
        bg={style.bg}
        cursor={readOnly ? 'not-allowed' : 'pointer'}
        transition="background-color 0.15s, border-color 0.15s"
        _hover={readOnly ? undefined : { bg: 'accent.bg' }}
        _focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', outlineOffset: '2px' }}
      >
        <Flex
          w="44px"
          h="44px"
          flexShrink={0}
          borderRadius="xl"
          bg={style.tileBg}
          align="center"
          justify="center"
        >
          <MaterialIcon name={style.icon ?? icon} boxSize={6} color={style.ink} />
        </Flex>
        <Text fontWeight="semibold" fontSize="md" color="text.primary">
          {headline}
        </Text>
        <Box id={describedBy}>
          {detail && (
            <Text fontSize="sm" color="text.secondary" maxW="42ch" mx="auto">
              {detail}
            </Text>
          )}
          {look === 'idle' && !readOnly && <RuleChips intake={intake} />}
        </Box>
        {offersCamera && (
          <CameraButtons onTakePhoto={takePhoto} onChooseLibrary={chooseFromLibrary} />
        )}
        {intake.destinationLabel && look === 'idle' && (
          <Destination label={intake.destinationLabel} />
        )}
      </Stack>
      {/* Outside the card: an input inside a role="button" is nested interaction.
          Hidden from assistive tech: the card and its buttons are the controls,
          and react-dropzone 20 leaves the input in the tree at opacity 0. */}
      <input {...intake.getInputProps()} aria-label={title} aria-hidden="true" />
      {offersCamera && (
        <VisuallyHidden>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            aria-label="Take a photo"
            aria-hidden="true"
            tabIndex={-1}
            onChange={onCamera}
          />
        </VisuallyHidden>
      )}
      {uploads && uploads.length > 0 && (
        <Stack spacing={1.5} mt={2}>
          {uploads.map((upload) => (
            <UploadRow key={upload.id} upload={upload} />
          ))}
          <Text fontSize="xs" color="text.secondary">
            You can keep working. Progress also shows in the upload tray.
          </Text>
        </Stack>
      )}
      <VisuallyHidden role="status" aria-live="polite">
        {intake.liveText}
      </VisuallyHidden>
    </Box>
  );
}
